import { apiSlice } from "../../app/apiSlice";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import {
	mapFnBRestaurantDetail,
	mapFnBRestaurantListItem,
} from "./fnb.mappers";
import type {
	FnBRestaurantSortBy,
	FnBRestaurantUpsertPayload,
	FnBVenueStatus,
} from "./fnb.types";
import type { RestaurantDetail, RestaurantListItem } from "./restaurant.types";

export interface FnBRestaurantsListMeta {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
}

export interface FnBRestaurantsListResponse {
	lastSyncedAt?: string;
	restaurants: RestaurantListItem[];
	pagination: FnBRestaurantsListMeta;
}

export interface GetFnBRestaurantsArgs {
	page?: number;
	limit?: number;
	sortBy?: FnBRestaurantSortBy | string;
	sortOrder?: "asc" | "desc";
	search?: string;
	source?: string;
	status?: string;
}

export interface UpdateFnBRestaurantRequest {
	id: string;
	body: FnBRestaurantUpsertPayload;
}

function parseListResponse(raw: unknown): FnBRestaurantsListResponse {
	const root = (raw ?? {}) as Record<string, unknown>;
	const data =
		root.data && typeof root.data === "object"
			? (root.data as Record<string, unknown>)
			: {};
	const restaurantsRaw = Array.isArray(data.restaurants)
		? data.restaurants
		: [];
	const paginationRaw =
		data.pagination && typeof data.pagination === "object"
			? (data.pagination as Record<string, unknown>)
			: {};

	const lastSyncedAt =
		typeof data.lastSyncedAt === "string" ? data.lastSyncedAt : undefined;

	return {
		lastSyncedAt,
		restaurants: restaurantsRaw.map((entry) => mapFnBRestaurantListItem(entry)),
		pagination: {
			page: Number(paginationRaw.page ?? 1),
			limit: Number(paginationRaw.limit ?? 10),
			total: Number(paginationRaw.total ?? restaurantsRaw.length),
			totalPages: Number(paginationRaw.totalPages ?? 1),
			hasPrevious: Boolean(paginationRaw.hasPrevious ?? false),
			hasNext: Boolean(paginationRaw.hasNext ?? false),
		},
	};
}

function parseDetailResponse(raw: unknown): RestaurantDetail {
	const root = (raw ?? {}) as Record<string, unknown>;
	const data =
		root.data && typeof root.data === "object" && !Array.isArray(root.data)
			? root.data
			: root;
	return mapFnBRestaurantDetail(data);
}

export const fnbRestaurantsApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getFnBRestaurants: builder.query<
			FnBRestaurantsListResponse,
			GetFnBRestaurantsArgs | undefined
		>({
			query: (args) => {
				const params: Record<string, string | number> = {
					page: args?.page ?? 1,
					limit: args?.limit ?? 10,
					sortBy: args?.sortBy ?? "DEFAULT",
					sortOrder: args?.sortOrder ?? "desc",
				};
				if (args?.search?.trim()) params.search = args.search.trim();
				if (args?.source && args.source !== "ALL") params.source = args.source;
				if (args?.status && args.status !== "ALL") {
					params.status = args.status;
				}
				return {
					url: API_ENDPOINTS.fnbRestaurants.list,
					params,
				};
			},
			transformResponse: parseListResponse,
			providesTags: (result) =>
				result
					? [
							{ type: "FnBRestaurant", id: "LIST" },
							...result.restaurants.map((r) => ({
								type: "FnBRestaurant" as const,
								id: r.id,
							})),
						]
					: [{ type: "FnBRestaurant", id: "LIST" }],
		}),
		getFnBRestaurantById: builder.query<RestaurantDetail, string>({
			query: (id) => API_ENDPOINTS.fnbRestaurants.detail(id),
			transformResponse: parseDetailResponse,
			providesTags: (_result, _err, id) => [{ type: "FnBRestaurant", id }],
		}),
		createFnBRestaurant: builder.mutation<
			RestaurantDetail,
			FnBRestaurantUpsertPayload
		>({
			query: (body) => ({
				url: API_ENDPOINTS.fnbRestaurants.create,
				method: "POST",
				body,
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: [{ type: "FnBRestaurant", id: "LIST" }],
		}),
		updateFnBRestaurant: builder.mutation<
			RestaurantDetail,
			UpdateFnBRestaurantRequest
		>({
			query: ({ id, body }) => ({
				url: API_ENDPOINTS.fnbRestaurants.update(id),
				method: "PATCH",
				body,
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, { id }) => [
				{ type: "FnBRestaurant", id },
				{ type: "FnBRestaurant", id: "LIST" },
			],
		}),
		publishFnBRestaurant: builder.mutation<RestaurantDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.fnbRestaurants.publish(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "FnBRestaurant", id },
				{ type: "FnBRestaurant", id: "LIST" },
			],
		}),
		deactivateFnBRestaurant: builder.mutation<RestaurantDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.fnbRestaurants.deactivate(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "FnBRestaurant", id },
				{ type: "FnBRestaurant", id: "LIST" },
			],
		}),
		activateFnBRestaurant: builder.mutation<RestaurantDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.fnbRestaurants.activate(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "FnBRestaurant", id },
				{ type: "FnBRestaurant", id: "LIST" },
			],
		}),
		deleteFnBRestaurant: builder.mutation<void, string>({
			query: (id) => ({
				url: API_ENDPOINTS.fnbRestaurants.delete(id),
				method: "DELETE",
			}),
			invalidatesTags: (_result, _err, id) => [
				{ type: "FnBRestaurant", id },
				{ type: "FnBRestaurant", id: "LIST" },
			],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetFnBRestaurantsQuery,
	useGetFnBRestaurantByIdQuery,
	useCreateFnBRestaurantMutation,
	useUpdateFnBRestaurantMutation,
	usePublishFnBRestaurantMutation,
	useDeactivateFnBRestaurantMutation,
	useActivateFnBRestaurantMutation,
	useDeleteFnBRestaurantMutation,
} = fnbRestaurantsApi;

export type { FnBVenueStatus };
